import {
  REENCRYPT_BATCH,
  exportSpaceKey,
  generateRecoveryCode,
  generateSpaceKey,
  importSpaceKey,
  normaliseRecoveryCode,
  unwrapSpaceKey,
  wrapSpaceKey,
  type ContentBlob,
  type EncryptableType,
  type ReencryptRequest,
} from "@vmeste/shared";
import { api } from "./api.js";
import { syncEngine } from "./sync.js";
import { loadSpaceKey, saveSpaceKey, useKeyFor } from "./spacekeys.js";

/**
 * Turning a space's encryption on, opening it on a second device, and moving
 * what was written before across (SPEC §3).
 *
 * The crypto itself lives in `@vmeste/shared/spacekey`. What lives here is the
 * order the steps have to happen in — which is the part that is easy to get
 * wrong in a way nobody notices until the content is unreadable.
 */

/* ------------------------------------------------------------ turning it on */

export interface EnabledSpace {
  /** Shown once, and never obtainable again from anywhere. */
  recoveryCode: string;
}

/**
 * Switch a space to `e2ee`.
 *
 * The order matters and is not interchangeable:
 *
 *   1. mint the key and the recovery code, and wrap one with the other;
 *   2. tell the server, which stores a blob it cannot open;
 *   3. only then keep the key on this device.
 *
 * Saving the key first would leave a device holding the key to a space the
 * server still thinks is plaintext — every write after that would go out in the
 * clear under a codec that believes otherwise. Telling the server first means
 * the worst case is a space that is locked and a device that must use the
 * recovery code, which is recoverable; the other way round is not.
 *
 * Existing content is **not** touched here. See `reencryptSpace`.
 */
export async function enableEncryption(spaceId: string): Promise<EnabledSpace> {
  const recoveryCode = generateRecoveryCode();
  const key = await generateSpaceKey();
  const wrappedKey = await wrapSpaceKey(key, normaliseRecoveryCode(recoveryCode));

  await api.enableEncryption(spaceId, wrappedKey);

  await saveSpaceKey(spaceId, key);
  useKeyFor(spaceId, key);

  return { recoveryCode };
}

/**
 * Mint a new recovery code for a space this device can already open.
 *
 * The old one dies the moment this returns, and the screen calling it has to
 * say so: replacing a working backup with a dead one, quietly, is the failure
 * this feature can actually cause.
 *
 * The key never changes and nothing is re-encrypted — that is the whole reason
 * the space key and the code are two different things. Only the wrapping is
 * redone, which is one blob.
 *
 * Refuses outright when this device holds no key: wrapping a key we do not have
 * is not possible, and minting a code that opens nothing would be worse than
 * saying no.
 */
export async function reissueRecoveryCode(spaceId: string): Promise<string> {
  const key = await loadSpaceKey(spaceId);
  if (!key) throw new Error("this device has no key for this space");

  const recoveryCode = generateRecoveryCode();
  const wrappedKey = await wrapSpaceKey(key, normaliseRecoveryCode(recoveryCode));

  await api.reissueRecoveryKey(spaceId, wrappedKey);

  return recoveryCode;
}

/* --------------------------------------------------------------- unlocking */

/**
 * Open a locked space with the recovery code.
 *
 * The wrapped blob comes from the space itself, which every member can read —
 * it is meaningless without the code, which is the entire design.
 */
export async function unlockWithRecoveryCode(
  spaceId: string,
  code: string,
): Promise<void> {
  const { space } = await api.space(spaceId);
  if (!space.wrappedKey) throw new Error("this space has no key to open");

  const key = await unwrapSpaceKey(space.wrappedKey, normaliseRecoveryCode(code));

  await saveSpaceKey(spaceId, key);
  useKeyFor(spaceId, key);
}

/**
 * Open a locked space with a key handed over directly — the QR case.
 *
 * No server round trip at all: the material never left the two phones. What
 * arrives here came out of a URL fragment, which browsers do not send.
 */
export async function unlockWithKeyMaterial(
  spaceId: string,
  material: string,
): Promise<void> {
  const key = await importSpaceKey(material);

  await saveSpaceKey(spaceId, key);
  useKeyFor(spaceId, key);
}

/**
 * The key material inside a scanned square, or `null` if there is none for this
 * space.
 *
 * The whole of what a scanner is allowed to trust. Two refusals matter and are
 * not defensive padding:
 *
 * **A square from another space is refused**, not applied. Storing it would put
 * a working key under the wrong space id, and everything read through it
 * afterwards would be gibberish with no explanation on screen — far worse than
 * a scan that says "not this one".
 *
 * **Anything that is not one of our links is refused.** A camera pointed at the
 * world reads posters, packaging and other people's Wi-Fi; a scanner that took
 * whatever it saw as key material would be a scanner that can be handed a key
 * by a sticker.
 */
export interface ScannedJoin {
  spaceId: string;
  /** Present when the square also lets the person in. */
  invite: string | null;
  material: string;
}

/**
 * A scanned square, read by somebody who is not in the space yet.
 *
 * The lock screen knows which space it is asking about and refuses anything
 * else. The sign-in screen cannot: whoever is standing there has no space to
 * compare against, and the whole point of the square they were handed is that
 * it names one. So this checks the shape and leaves the deciding to `/join`,
 * which is the screen that knows how to let somebody in.
 *
 * Same-origin and `/join` are still required, for the same reason as ever: a
 * camera pointed at the world reads posters and packaging, and a scanner that
 * followed whatever it saw would be a scanner that can be sent anywhere by a
 * sticker.
 */
export function joinFromScan(text: string): ScannedJoin | null {
  let url: URL;
  try {
    url = new URL(text, window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname !== "/join") return null;

  const spaceId = url.searchParams.get("space");
  const material = new URLSearchParams(url.hash.slice(1)).get("k");
  if (!spaceId || !material) return null;

  return { spaceId, invite: url.searchParams.get("invite"), material };
}

export function keyFromScan(spaceId: string, text: string): string | null {
  let url: URL;
  try {
    url = new URL(text, window.location.origin);
  } catch {
    return null;
  }

  if (url.origin !== window.location.origin) return null;
  if (url.pathname !== "/join") return null;
  if (url.searchParams.get("space") !== spaceId) return null;

  // After the "#", where it has always been: the fragment is the one part of a
  // URL browsers never put on the wire.
  const material = new URLSearchParams(url.hash.slice(1)).get("k");
  return material && material.length > 0 ? material : null;
}

/**
 * The key this device holds, as a QR code's worth of text.
 *
 * Only ever called to draw something a person is looking at. It is why an
 * unwrapped key is extractable — see the note on `unwrapSpaceKey`.
 */
export async function keyMaterialFor(spaceId: string): Promise<string | null> {
  const key = await loadSpaceKey(spaceId);
  return key ? exportSpaceKey(key) : null;
}

/* ------------------------------------------------------------ catching up */

export interface ReencryptProgress {
  /** Rows still carrying plaintext when the pass started. */
  total: number;
  /** Rows rewritten so far. */
  done: number;
}

type Row = ReencryptRequest["rows"][number];

/** A row still in the clear, with the value that has to be rewritten. */
interface PlaintextRow {
  type: EncryptableType;
  id: string;
  value: unknown;
}

/** Rows whose stored content is still readable to whoever holds the database. */
function plaintextRows(spaceId: string): PlaintextRow[] {
  const state = syncEngine.view(spaceId);
  const found: PlaintextRow[] = [];

  const take = (type: EncryptableType, id: string, content: ContentBlob | null) => {
    // Narrowed here rather than at the far end: what gets re-encrypted is the
    // `data` of a plaintext blob, and nothing else in this file has to know
    // that a blob comes in two shapes.
    if (content?.mode === "none") found.push({ type, id, value: content.data });
  };

  if (state.space) take("space", state.space.id, state.space.content);
  for (const row of Object.values(state.lists)) take("list", row.id, row.content);
  for (const row of Object.values(state.items)) take("item", row.id, row.content);
  for (const row of Object.values(state.wishes)) take("wish", row.id, row.content);
  for (const row of Object.values(state.messages)) take("message", row.id, row.content);
  for (const row of Object.values(state.events)) take("event", row.id, row.content);
  for (const row of Object.values(state.topics)) take("topic", row.id, row.content);
  for (const row of Object.values(state.places)) take("place", row.id, row.content);
  for (const row of Object.values(state.placeReviews)) {
    take("review", row.id, row.content);
  }
  for (const row of Object.values(state.tags)) take("tag", row.id, row.content);

  return found;
}

/** How much of this space is still in the clear. Zero means the pass is done. */
export function plaintextCount(spaceId: string): number {
  return plaintextRows(spaceId).length;
}

/**
 * Rewrite everything written before the switch, in batches.
 *
 * Resumable by construction, and it has to be: this walks a year of two
 * people's lists over a phone connection, and there is no moment at which the
 * whole space can be assumed to have made it. A pass that dies halfway leaves
 * the rest in the clear and readable; running it again finishes the job,
 * because "still plaintext" is a property of the rows themselves rather than a
 * cursor kept somewhere.
 *
 * The plaintext is read out of the blob rather than the decoded view: `mode:
 * "none"` blobs carry `data` verbatim, so the value re-encrypted is exactly the
 * value that was stored, with no schema in the middle to drop a field this
 * version of the client happens not to know about.
 */
export async function reencryptSpace(
  spaceId: string,
  onProgress?: (progress: ReencryptProgress) => void,
): Promise<ReencryptProgress> {
  const codec = syncEngine.codec(spaceId);
  if (!codec || codec.mode !== "e2ee") {
    throw new Error("this space is not encrypted on this device");
  }

  const total = plaintextCount(spaceId);
  let done = 0;
  onProgress?.({ total, done });

  // Re-read the state each round rather than walking one snapshot: a delta
  // landing mid-pass is normal, and a stale list would send rows the server has
  // already rewritten.
  for (;;) {
    const pending = plaintextRows(spaceId).slice(0, REENCRYPT_BATCH);
    if (pending.length === 0) break;

    const rows: Row[] = [];
    for (const row of pending) {
      rows.push({ type: row.type, id: row.id, content: await codec.encode(row.value) });
    }

    const result = await api.reencrypt(spaceId, rows);

    done += result.rewritten;
    onProgress?.({ total: Math.max(total, done), done });

    /*
     * Nothing moved, and the rows are still here.
     *
     * That means the server skipped every one of them — it disagrees with this
     * device about what is still plaintext, and going round again would send
     * the same batch for ever. Stopping leaves the pass unfinished, which is a
     * state this design already handles; a spin loop is not.
     */
    if (result.rewritten === 0) break;

    await syncEngine.pull(spaceId);
  }

  return { total: Math.max(total, done), done };
}
